import { Box, Button, Card, Typography } from "@mui/material";

import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";

export const SectionTitle = styled(Typography)`
    && {
        font-family: Nunito, sans-serif;
        font-size: 20px;
        font-weight: 800;
        margin-bottom: 16px;
        color: #1a1a1a;
    }
`;

export const SectionBlock = styled(Box)`
    margin-bottom: 32px;
`;

/* Column count is the visible card count capped at what fits on the viewport,
 * so a single row always spans the full width while any card pushed onto a
 * following row keeps that same column width instead of stretching. */
const columns = (max) => (props) =>
    `repeat(${Math.max(1, Math.min(props.$count ?? max, max))}, minmax(0, 1fr))`;

export const AnalyticsCardsRow = styled(Box)`
    display: grid;
    gap: 16px;
    grid-template-columns: ${columns(5)};

    @media (max-width: 1600px) {
        grid-template-columns: ${columns(4)};
    }

    @media (max-width: 1400px) {
        grid-template-columns: ${columns(3)};
    }

    @media (max-width: 900px) {
        grid-template-columns: ${columns(2)};
    }

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

/* Flex, not grid, so a partially filled row still spans the full width: the
 * slots share the leftover space instead of leaving empty tracks. Each slot's
 * flex-basis is the width it would have in a full row of four, and flex-grow
 * lets fewer-than-four cards stretch to cover the rest. */
export const CardsRow = styled(Box)`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: stretch;
`;

export const CardSlot = styled(Box)`
    min-width: 0;
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: calc(25% - 12px);

    @media (max-width: 1400px) {
        flex-basis: calc(33.333% - 10.667px);
    }

    @media (max-width: 1100px) {
        flex-basis: calc(50% - 8px);
    }

    @media (max-width: 600px) {
        flex-basis: 100%;
    }
`;

export const StatCardSlot = styled(Box)`
    min-width: 0;
`;

const shimmer = keyframes`
    100% {
        transform: translateX(100%);
    }
`;

export const OverviewCardSkeleton = styled(Box)`
    position: relative;
    /* Fill the slot so the skeleton matches the height of the loaded cards
       sharing the row; 220px is only the floor when it stands alone. */
    height: 100%;
    min-height: 220px;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    background: #f2f2f5;
    overflow: hidden;

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        transform: translateX(-100%);
        background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.7),
            transparent
        );
        animation: ${shimmer} 1.4s ease-in-out infinite;
    }
`;

export const DashboardCard = styled(Card)`
    && {
        border-radius: 10px;
        border: 1px solid rgba(0, 0, 0, 0.05);
        box-shadow: none;
        position: relative;
        overflow: hidden;
        height: 100%;
        display: flex;
        flex-direction: column;
        transition: transform 0.2s ease;

        &:hover {
            transform: translateY(-2px);
        }
    }
`;

const OVERVIEW_TONES = {
    neutral: {
        accent: "rgba(0, 0, 0, 0.02)",
        chipBg: "rgba(0, 0, 0, 0.02)",
        chipFg: "#000000",
        title: "inherit",
        body: "rgba(0, 0, 0, 0.6)",
        action: "#000000",
    },
    warning: {
        accent: "rgba(237, 108, 2, 0.02)",
        chipBg: "rgba(237, 108, 2, 0.04)",
        chipFg: "#ED6C02",
        title: "#ED6C02",
        body: "#ED6C02",
        action: "#ED6C02",
    },
    danger: {
        accent: "rgba(255, 0, 0, 0.02)",
        chipBg: "rgba(255, 0, 0, 0.02)",
        chipFg: "#FF0000",
        title: "#FF0000",
        body: "#FF0000",
        action: "#FF0000",
    },
};

const tone = ({ $tone }) => OVERVIEW_TONES[$tone] ?? OVERVIEW_TONES.neutral;

export const CardAccent = styled(Box)`
    position: absolute;
    top: -40px;
    right: -40px;
    width: 128px;
    height: 128px;
    border-radius: 50%;
    background-color: ${(props) => tone(props).accent};
    filter: blur(20px);
    pointer-events: none;
`;

export const OverviewCardContent = styled(Box)`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 16px;
    padding: 16px;
    height: 100%;
    min-width: 0;
    box-sizing: border-box;
    overflow: hidden;
`;

export const OverviewIconChip = styled(Box)`
    width: fit-content;
    padding: 12px;
    border-radius: 10px;
    background-color: ${(props) => tone(props).chipBg};
    color: ${(props) => tone(props).chipFg};
    display: flex;
    flex-shrink: 0;
`;

export const OverviewCardHeader = styled(Box)`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
`;

export const OverviewCardTitle = styled(Typography)`
    && {
        font-family: Nunito, sans-serif;
        font-size: clamp(15px, 1.4vw, 18px);
        font-weight: 700;
        min-width: 0;
        color: ${(props) => tone(props).title};
        word-break: break-word;
    }
`;

export const OverviewCardTitleText = styled.span`
    min-width: 0;
    flex: 1;
`;

export const OverviewCardBody = styled(Typography)`
    && {
        font-family: Nunito, sans-serif;
        font-size: 14px;
        color: ${(props) => tone(props).body};
        min-width: 0;
        word-break: break-word;
    }
`;

export const OverviewCardActions = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    margin-top: auto;
    min-width: 0;
    color: ${(props) => tone(props).action};
    transition: opacity 0.2s ease;
    &:hover {
        opacity: 0.65;
    }
`;

export const OverviewActionLabel = styled(Typography)`
    && {
        font-family: Nunito, sans-serif;
        font-size: 0.875rem;
        font-weight: 700;
        color: inherit;
    }
`;

export const OverviewActionLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    color: inherit;
    text-decoration: none;
    min-width: 0;
    max-width: 100%;

    &:hover {
        opacity: 0.85;
    }
`;

export const OverviewActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    margin: 0;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    color: inherit;
    min-width: 0;
    max-width: 100%;
    font: inherit;

    &:hover {
        opacity: 0.85;
    }
`;

export const PrimaryCardButton = styled(Button)`
    && {
        min-width: 0;
        max-width: 100%;
        border: 0;
        font-family: Nunito, sans-serif;
        font-weight: 700;
        text-transform: none;
        color: #000000;
        background-color: transparent;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &:hover {
            background-color: rgba(0, 0, 0, 0.04);
            border: 0;
        }

        a {
            color: inherit;
            text-decoration: none;
        }
    }
`;

export const ReportsHighlight = styled(Box)`
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
    padding: 12px 14px;
    border-radius: 10px;
    background-color: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.08);
`;

export const ReportsCount = styled(Typography)`
    && {
        font-family: Nunito, sans-serif;
        font-size: clamp(28px, 3.2vw, 40px);
        font-weight: 800;
        line-height: 1;
        color: #000000;
        flex-shrink: 0;
        min-width: 1.2em;
        text-align: center;
    }
`;

export const ReportsHighlightCopy = styled(Box)`
    min-width: 0;
    flex: 1;
    overflow: hidden;
`;

export const ReportsHighlightTitle = styled(Typography)`
    && {
        font-family: Nunito, sans-serif;
        font-size: 14px;
        font-weight: 800;
        color: #1a1a1a;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

export const ReportsHighlightSubtitle = styled(Typography)`
    && {
        font-family: Nunito, sans-serif;
        font-size: 13px;
        color: rgba(0, 0, 0, 0.55);
        margin-top: 2px;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        word-break: break-word;
    }
`;

export const ReportsMetaRow = styled(Box)`
    display: flex;
    align-items: center;
    min-height: 56px;
    min-width: 0;
    gap: 8px;
    overflow: hidden;
`;

export const ReportsCountWrap = styled(Box)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-width: 48px;
    max-width: 80px;
    flex-shrink: 0;
`;

export const ReportsNamesWrap = styled(Box)`
    flex: 1;
    min-width: 0;
    overflow: hidden;
`;

export const TrackerName = styled(Typography)`
    && {
        font-family: Nunito, sans-serif;
        font-size: 14px;
        color: #444;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
    }
`;

export const StatLabel = styled(Typography)`
    && {
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: rgba(0, 0, 0, 0.6);
        font-family: Nunito, sans-serif;
        min-width: 0;
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding-right: 8px;
    }
`;

export const StatValue = styled(Typography)`
    && {
        position: relative;
        display: inline-block;
        font-size: clamp(18px, 2.4vw, 32px);
        font-weight: 700;
        font-family: Nunito, sans-serif;
        margin-top: 8px;
        min-width: 0;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        line-height: 1.15;
    }
`;

export const StatIconChip = styled(Box)`
    color: ${({ $color }) => $color};
    background-color: ${({ $bg }) => $bg};
    border-radius: 8px;
    padding: 8px;
    display: flex;
    flex-shrink: 0;
`;

export const StatHeader = styled(Box)`
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
`;

export const ReportsList = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
`;

export const ReportListItem = styled(Link)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    text-decoration: none;
    color: inherit;
    background: #ffffff;
    transition:
        background-color 0.15s ease,
        border-color 0.15s ease;

    &:hover {
        background-color: rgba(237, 108, 2, 0.04);
        border-color: rgba(237, 108, 2, 0.35);
    }
`;

export const ReportListItemName = styled(Typography)`
    && {
        font-family: Nunito, sans-serif;
        font-size: 15px;
        font-weight: 700;
        line-height: 1.3;
    }
`;

export const ReportListItemMeta = styled(Typography)`
    && {
        font-family: Nunito, sans-serif;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.45);
        margin-top: 2px;
    }
`;

export const ReportListItemChevron = styled(Box)`
    display: flex;
    align-items: center;
    color: rgba(0, 0, 0, 0.35);
    flex-shrink: 0;
`;

export const HomeTrackersMobile = styled(Box)`
    width: 100%;
    min-width: 0;
`;

export const ShowMoreTrackersWrap = styled(Box)`
    display: flex;
    justify-content: center;
    margin-top: 4px;
    margin-bottom: 8px;
`;
